import Vue from 'vue'
import Vuex from 'vuex'
import _ from 'lodash'
import { DateTime } from 'luxon'

Vue.use(Vuex)

/*
  TODO:
    - Error handling/display
    - FUTURE: Poll for update timestamps <OR> websocket to receive update notification
*/

const internals = {
  $api: {},
  meta: {
    showNoCents: false
  },
  assertPresent (value, errorMsg) {
    if (!value) {
      throw new Error(errorMsg)
    }
  },
  processAudited (entityList) {
    const entries = {} // not deleted
    const deleted = {} // deleted
    let lastUpdatedAt = DateTime.fromMillis(0)
    entityList.forEach(e => {
      if (e.isDeleted) {
        deleted[`${e.id}`] = e
      } else {
        entries[`${e.id}`] = e
      }
      if (lastUpdatedAt < e.updatedAt) {
        lastUpdatedAt = e.updatedAt
      }
    })
    return { entries, deleted, lastUpdatedAt }
  },
  newAuditedModelStore (baseObj) {
    return _.assign({
      loading: false,
      entries: {}, // Keyed by id-as-string
      deleted: {}, // Keyed by id-as-string
      lastUpdatedAt: null
    }, baseObj)
  }
}

const store = new Vuex.Store({
  state: {
    initComplete: false,
    auth: {
      hasKey: false
    },
    items: internals.newAuditedModelStore({ allLoaded: false }),
    categories: internals.newAuditedModelStore(),
    meals: internals.newAuditedModelStore(),
    stores: internals.newAuditedModelStore()
  },
  mutations: {
    // region - Mutations: Meta/Auth
    INIT_DONE (state) {
      state.initComplete = true
    },
    SET_HAS_AUTH_KEY (state, { hasKey }) {
      state.auth.hasKey = hasKey
    },
    // endregion
    // region - Mutations: Items
    SET_ITEMS_LOADING (state, { areLoading }) {
      state.items.loading = areLoading
    },
    SET_ITEMS (state, { entries, neededOnly }) {
      // separate into entries/deleted and extract last updated-at
      const result = internals.processAudited(entries)
      if (state.items.lastUpdatedAt && state.items.lastUpdatedAt > result.lastUpdatedAt) {
        // The last updated-at timestamp is later than this (new) data...
        return
      }
      // Newer data, set timestamp
      state.items.lastUpdatedAt = result.lastUpdatedAt
      if (!neededOnly) {
        // All items, overwrite entire map
        state.items.allLoaded = true
        state.items.entries = result.entries
        state.items.deleted = result.deleted
        return
      }
      // Only needed items, so do merged update
      // NOTE: Needed items don't include deleted!
      const neededIds = _.keys(result.entries)
      state.items.entries.forEach((item, itemId) => {
        if (!_.includes(neededIds, itemId)) {
          item.quantityNeeded = 0
        }
      })
      // Put all needed items in item entries list
      result.entries.forEach((item, itemId) => {
        state.items.entries[itemId] = item
        // Handle restored deleted items (now needed)
        delete state.items.deleted[itemId]
      })
    },
    SET_ITEM_CART_STATUS (state, { id, isInCart, updatedAt }) {
      const item = state.items.entries[id]
      if (!item) {
        return
      }
      item.isInCart = isInCart
      item.updatedAt = updatedAt
      state.items.lastUpdatedAt = updatedAt
    },
    SET_ITEM_QUANTITY_NEEDED (state, { id, quantityNeeded, updatedAt }) {
      const item = state.items.entries[id]
      if (!item) {
        return
      }
      item.quantityNeeded = quantityNeeded
      item.totalPriceCents = item.priceEachCents * quantityNeeded
      item.hasNoPrice = !item.priceEachCents
      item.updatedAt = updatedAt
      state.items.lastUpdatedAt = updatedAt
    },
    ADD_ITEM (state, item) {
      item.totalPriceCents = item.priceEachCents * item.quantityNeeded
      item.hasNoPrice = !item.priceEachCents
      item.storeIds = item.storeIds || []

      if (item.deletedAt) {
        Vue.set(state.items.deleted, item.id, item)
      } else {
        Vue.set(state.items.entries, item.id, item)
      }

      state.items.lastUpdatedAt = item.updatedAt
    },
    UPDATE_ITEM (state, { id, name, notes, priceEachCents, isOneTimeItem, isEventualPurchase, quantityNeeded, categoryId, storeIds, updatedAt }) {
      const item = state.items.entries[id]
      if (!item) {
        return
      }
      item.name = name
      item.notes = notes
      item.priceEachCents = priceEachCents
      item.isOneTimeItem = isOneTimeItem
      item.isEventualPurchase = isEventualPurchase
      item.categoryId = categoryId
      item.quantityNeeded = quantityNeeded
      item.totalPriceCents = item.priceEachCents * quantityNeeded
      item.hasNoPrice = !item.priceEachCents
      item.storeIds = storeIds || []
      item.updatedAt = updatedAt
      // Remove item from cart if none needed
      if (!item.quantityNeeded) {
        item.isInCart = false
      }
      state.items.lastUpdatedAt = updatedAt
    },
    // endregion
    // region - Mutations: Categories
    SET_CATEGORIES_LOADING (state, { areLoading }) {
      state.categories.loading = areLoading
    },
    SET_CATEGORIES (state, { entries }) {
      const result = internals.processAudited(entries)
      if (!state.categories.lastUpdatedAt || state.categories.lastUpdatedAt < result.lastUpdatedAt) {
        state.categories.entries = result.entries
        state.categories.deleted = result.deleted
        state.categories.lastUpdatedAt = result.lastUpdatedAt
      }
    },
    // endregion
    // region - Mutations: Meals
    SET_MEALS_LOADING (state, { areLoading }) {
      state.meals.loading = areLoading
    },
    SET_MEALS (state, { entries }) {
      const result = internals.processAudited(entries)
      if (!state.meals.lastUpdatedAt || state.meals.lastUpdatedAt < result.lastUpdatedAt) {
        state.meals.entries = result.entries
        state.meals.deleted = result.deleted
        state.meals.lastUpdatedAt = result.lastUpdatedAt
      }
    },
    ADD_MEAL (state, meal) {
      if (meal.deletedAt) {
        Vue.set(state.meals.deleted, meal.id, meal)
      } else {
        Vue.set(state.meals.entries, meal.id, meal)
      }
      state.meals.lastUpdatedAt = meal.updatedAt
    },
    UPDATE_MEAL (state, { id, name, ingredients, updatedAt, deletedAt }) {
      const meal = state.meals.entries[id]
      if (!meal) {
        return
      }
      meal.name = name
      meal.ingredients = ingredients
      meal.updatedAt = updatedAt
      if (meal.deletedAt && !deletedAt) {
        // Move to full list
        Vue.set(state.meals.entries, meal.id, meal)
        Vue.delete(state.meals.deleted, meal.id)
      } else if (!meal.deletedAt && deletedAt) {
        // Move to deleted list
        Vue.set(state.meals.deleted, meal.id, meal)
        Vue.delete(state.meals.entries, meal.id)
      }
      meal.deletedAt = deletedAt
      state.meals.lastUpdatedAt = updatedAt
    },
    // endregion
    // region - Mutations: Stores
    SET_STORES_LOADING (state, { areLoading }) {
      state.stores.loading = areLoading
    },
    SET_STORES (state, { entries }) {
      const result = internals.processAudited(entries)
      if (!state.stores.lastUpdatedAt || state.stores.lastUpdatedAt < result.lastUpdatedAt) {
        state.stores.entries = result.entries
        state.stores.deleted = result.deleted
        state.stores.lastUpdatedAt = result.lastUpdatedAt
      }
    }
    // endregion
  },
  actions: {
    // region - Actions: Auth
    async signIn ({ commit }, { key }) {
      await internals.$api.setAuthKey(key)
      const hasKey = await internals.$api.hasValidAuthKey()
      commit('SET_HAS_AUTH_KEY', { hasKey })
      return hasKey
    },
    async signOut ({ commit }) {
      await internals.$api.clearAuthKey()
      commit('SET_HAS_AUTH_KEY', { hasKey: false })
    },
    // endregion
    // region - Actions: Init
    async initialize ({ commit, dispatch }) {
      internals.$api = internals.$api || this._vm.$api
      const hasKey = await internals.$api.hasValidAuthKey()
      commit('SET_HAS_AUTH_KEY', { hasKey })
      if (hasKey) {
        dispatch('getInitialData')
      }
      commit('INIT_DONE')
      return hasKey
    },
    async getInitialData ({ dispatch }) {
      dispatch('refreshItems')
      dispatch('refreshCategories')
      dispatch('refreshMeals')
      dispatch('refreshStores')
    },
    // endregion
    // region - Actions: Items
    async refreshItems ({ commit }) {
      try {
        commit('SET_ITEMS_LOADING', { areLoading: true })
        const entries = await internals.$api.getAllItems()
        commit('SET_ITEMS', { entries, neededOnly: false })
      } catch (e) {
        console.error(`Error getting all items`, e)
      } finally {
        commit('SET_ITEMS_LOADING', { areLoading: false })
      }
    },
    async refreshNeededItems ({ commit }) {
      try {
        commit('SET_ITEMS_LOADING', { areLoading: true })
        const entries = await internals.$api.getNeededItems()
        commit('SET_ITEMS', { entries, neededOnly: true })
      } catch (e) {
        console.error(`Error getting needed items`, e)
      } finally {
        commit('SET_ITEMS_LOADING', { areLoading: false })
      }
    },
    async setItemCartStatus ({ commit }, { id, isInCart }) {
      try {
        internals.assertPresent(id, 'Item ID is required')
        const result = await internals.$api.setItemInCartForId({ id, isInCart })
        commit('SET_ITEM_CART_STATUS', { id, isInCart, updatedAt: result.updatedAt })
        // console.log(`Moved item ${id} ${isInCart ? 'to' : 'from'} cart: `, result)
      } catch (e) {
        console.error(`Error moving item ${id} ${isInCart ? 'to' : 'from'} cart`, e)
        // Revert
        isInCart = !isInCart
        commit('SET_ITEM_CART_STATUS', { id, isInCart })
      }
    },
    async setItemQuantityNeeded ({ commit }, { id, quantityNeeded }) {
      try {
        internals.assertPresent(id, 'Item ID is required')
        const result = await internals.$api.setItemQuantityForId({ id, quantityNeeded })
        commit('SET_ITEM_QUANTITY_NEEDED', { id, quantityNeeded, updatedAt: result.updatedAt })
        // console.log(`Set item ${id} quantity to ${quantityNeeded}: `, result)
      } catch (e) {
        console.error(`Error setting item ${id} quantity to ${quantityNeeded}`, e)
      }
    },
    async updateItem ({ commit }, { id, name, notes, priceEachCents, isOneTimeItem, isEventualPurchase, quantityNeeded, categoryId, storeIds }) {
      const updatedItem = { id, name, notes, priceEachCents, isOneTimeItem, isEventualPurchase, quantityNeeded, categoryId, storeIds }
      try {
        internals.assertPresent(id, 'Item ID is required')
        internals.assertPresent(name, 'Item name is required')
        internals.assertPresent(categoryId, 'Item category is required')
        const result = await internals.$api.saveItem(updatedItem)
        commit('UPDATE_ITEM', result)
        // console.log(`Updated item ${id} (${JSON.stringify(updatedItem)}): `, result)
      } catch (e) {
        console.error(`Error updating item ${id} (${JSON.stringify(updatedItem)}): `, e)
      }
    },
    async createItem ({ commit }, { name, notes, priceEachCents, isOneTimeItem, isEventualPurchase, quantityNeeded, categoryId, storeIds }) {
      const newItem = { name, notes, priceEachCents, isOneTimeItem, isEventualPurchase, quantityNeeded, categoryId, storeIds }
      try {
        internals.assertPresent(name, 'Item name is required')
        internals.assertPresent(categoryId, 'Item category is required')
        const result = await internals.$api.saveItem(newItem)
        commit('ADD_ITEM', result)
        // console.log(`Created item (${JSON.stringify(newItem)}): `, result)
      } catch (e) {
        console.error(`Error creating item (${JSON.stringify(newItem)}): `, e)
      }
    },
    async purchaseItemsInCart ({ dispatch }) {
      try {
        const result = await internals.$api.purchaseItemsInCart()
        // console.log(`Purchased all items in cart: `, result)
        await dispatch('refreshItems', result)
      } catch (e) {
        console.error(`Error purchasing items in cart: `, e)
      }
    },
    // endregion
    // region - Actions: Categories
    async refreshCategories ({ commit }) {
      try {
        commit('SET_CATEGORIES_LOADING', { areLoading: true })
        const entries = await internals.$api.getAllCategories()
        commit('SET_CATEGORIES', { entries })
      } catch (e) {
        console.error(`Error getting categories`, e)
      } finally {
        commit('SET_CATEGORIES_LOADING', { areLoading: false })
      }
    },
    // endregion
    // region - Actions: Meals
    async refreshMeals ({ commit }) {
      try {
        commit('SET_MEALS_LOADING', { areLoading: true })
        const entries = await internals.$api.getAllMeals()
        commit('SET_MEALS', { entries })
      } catch (e) {
        console.error(`Error getting meals`, e)
      } finally {
        commit('SET_MEALS_LOADING', { areLoading: false })
      }
    },
    async updateMeal ({ commit }, { id, name, ingredients }) {
      const updatedMeal = { id, name, ingredients }
      try {
        internals.assertPresent(id, 'Meal ID is required')
        internals.assertPresent(name, 'Meal name is required')
        internals.assertPresent(ingredients.length, 'Meal ingredients are required')
        const result = await internals.$api.saveMeal(updatedMeal)
        commit('UPDATE_MEAL', result)
      } catch (e) {
        console.error(`Error updating meal ${id} (${JSON.stringify(updatedMeal)}): `, e)
      }
    },
    async createMeal ({ commit }, { name, ingredients }) {
      const newMeal = { name, ingredients }
      try {
        internals.assertPresent(name, 'Meal name is required')
        internals.assertPresent(ingredients.length, 'Meal ingredients are required')
        const result = await internals.$api.saveMeal(newMeal)
        commit('ADD_MEAL', result)
      } catch (e) {
        console.error(`Error creating meal (${JSON.stringify(newMeal)}): `, e)
      }
    },
    // endregion
    // region - Actions: Stores
    async refreshStores ({ commit }) {
      try {
        commit('SET_STORES_LOADING', { areLoading: true })
        const entries = await internals.$api.getAllStores()
        commit('SET_STORES', { entries })
      } catch (e) {
        console.error(`Error getting stores`, e)
      } finally {
        commit('SET_STORES_LOADING', { areLoading: false })
      }
    }
    // endregion
  },
  getters: {
    // region - Getters: Meta/Auth
    meta () {
      return _.assign({}, internals.meta)
    },
    isSignedIn (state) {
      return state.auth.hasKey
    },
    initComplete (state) {
      return state.initComplete
    },
    // endregion
    // region - Getters: Items
    itemsAreLoading (state) {
      return !!state.items.loading
    },
    itemsAreLoaded (state) {
      return state.items.allLoaded && !!state.items.lastUpdatedAt
    },
    items (state) {
      // Clone the list
      return _.chain(state.items.entries)
        .values()
        .map(m => _.assign({}, m))
        .sortBy(m => m.name.toLowerCase())
        .value()
    },
    itemsByCategoryId (state) {
      return _.chain(state.items.entries)
        .values()
        .map(i => _.assign({}, i))
        .groupBy('categoryId')
        // .sortBy(m => m.name.toLowerCase())
        .value()
    },
    itemsDeleted (state) {
      // Clone the list
      return _.chain(state.categories.deleted)
        .values()
        .map(m => _.assign({}, m))
        .sortBy(m => m.name.toLowerCase())
        .value()
    },

    neededItemsAreLoaded (state) {
      return !!state.items.lastUpdatedAt
    },
    neededItems (state) {
      // Clone the list
      return _.chain(state.items.entries)
        .values()
        .filter(i => i.quantityNeeded > 0)
        .map(m => _.assign({}, m))
        .sortBy(m => m.name.toLowerCase())
        .value()
    },
    neededItemsStores (state, getters) {
      const stores = {}
      _.forEach(getters.neededItems, i => {
        _.forEach(i.storeIds, sId => {
          stores[sId] = state.stores.entries[sId] || {}
        })
      })
      return _.sortBy(_.values(stores), s => {
        return (s && s.name) ? s.name.toLowerCase() : ''
      })
    },

    getItemForId: (state) => (id) => {
      const i = state.items.entries[`${parseInt(id, 10)}`]
      return i && _.assign({}, i)
    },
    searchItems: (state, getters) => (searchText) => {
      const searchPattern = new RegExp(`.*${searchText.replace(/[^a-zA-Z]/, '.*')}.*`, 'i')
      return _.chain(getters.items)
        .map(i => {
          i.category = getters.categoriesById[`${i.categoryId}`] || null
          i.stores = _.map(i.storeIds, sId => getters.getStoreForId(sId)) || []
          return i
        })
        .filter(i => {
          return searchPattern.test(i.name) || searchPattern.test(i.notes) || searchPattern.test(`${i.category.name} ${i.name}`)
        })
        .sortBy(i => `${i.category.name.toLowerCase()}::${i.name.toLowerCase()}`)
        .value()
    },
    // endregion
    // region - Getters: Categories
    categories (state) {
      // Clone the list
      return _.chain(state.categories.entries)
        .values()
        .map(m => _.assign({}, m))
        .sortBy(m => m.name.toLowerCase())
        .value()
    },
    categoriesById (state) {
      return _.chain(state.categories.entries)
        .values()
        .map(c => _.assign({}, c))
        .keyBy('id')
        .value()
    },
    getCategoryForId: (state) => (id) => {
      const cat = state.categories.entries[`${parseInt(id, 10)}`]
      return cat && _.assign({}, cat)
    },
    categoriesAreLoading (state) {
      return !!state.categories.loading
    },
    categoriesAreLoaded (state) {
      return !!state.categories.lastUpdatedAt
    },
    categoriesDeleted (state) {
      // Clone the list
      return _.chain(state.categories.deleted)
        .values()
        .map(m => _.assign({}, m))
        .sortBy(m => m.name.toLowerCase())
        .value()
    },
    // endregion
    // region - Getters: Meals
    meals (state) {
      return _.chain(state.meals.entries)
        .values()
        .map(m => _.assign({}, m, {
          // Drop out deleted ingredient entries
          ingredients: _.compact(_.map(m.ingredients, i => {
            if (i.deletedAt) {
              return null
            }
            return _.assign({}, i)
          }))
        }))
        .sortBy(m => m.name.toLowerCase())
        .value()
    },
    mealsAreLoading (state) {
      return !!state.meals.loading
    },
    mealsAreLoaded (state) {
      return !!state.meals.lastUpdatedAt
    },
    mealsDeleted (state) {
      // Clone the list
      return _.chain(state.meals.deleted)
        .values()
        .map(m => _.assign({}, m))
        .sortBy(m => m.name.toLowerCase())
        .value()
    },
    getMealForId: (state) => (id) => {
      const meal = state.meals.entries[`${parseInt(id, 10)}`]
      return meal && _.assign({}, meal, {
        // Drop out deleted ingredient entries
        ingredients: _.compact(_.map(meal.ingredients, i => {
          if (i.deletedAt) {
            return null
          }
          return _.assign({}, i)
        }))
      })
    },
    // endregion
    // region - Getters: Stores
    stores (state) {
      return _.chain(state.stores.entries)
        .values()
        .map(s => _.assign({}, s, {
          // Drop out deleted store-item entries
          storeItems: _.compact(_.map(s.storeItems, i => {
            if (i.deletedAt) {
              return null
            }
            return _.assign({}, i)
          }))
        }))
        .sortBy(s => s.name.toLowerCase())
        .value()
    },
    storesAreLoading (state) {
      return !!state.stores.loading
    },
    storesAreLoaded (state) {
      return !!state.stores.lastUpdatedAt
    },
    storesDeleted (state) {
      // Clone the list
      return _.chain(state.stores.deleted)
        .values()
        .map(m => _.assign({}, m))
        .sortBy(m => m.name.toLowerCase())
        .value()
    },
    getStoreForId: (state) => (id) => {
      const store = state.stores.entries[`${parseInt(id, 10)}`]
      return store && _.assign({}, store, {
        // Drop out deleted store-item entries
        storeItems: _.compact(_.map(store.storeItems, i => {
          if (i.deletedAt) {
            return null
          }
          return _.assign({}, i)
        }))
      })
    }
    // endregion
  }
})

export default {
  store,
  install (Vue, { showNoCents }) {
    internals.meta.showNoCents = !!showNoCents
    internals.$api = Vue.prototype.$api

    Vue.mixin({
      methods: {
        ...Vuex.mapActions([
          'initialize',
          'getInitialData',
          'signIn',
          'signOut',

          'refreshItems',
          'refreshNeededItems',

          'refreshCategories',

          'refreshMeals',
          'createMeal',
          'updateMeal',

          'refreshStores',

          'setItemCartStatus',
          'setItemQuantityNeeded',
          'updateItem',
          'createItem',
          'purchaseItemsInCart'
        ])
      },
      computed: {
        ...Vuex.mapGetters([
          'meta',
          'initComplete',
          'isSignedIn',

          'itemsAreLoading',
          'itemsAreLoaded',
          'items',
          'itemsByCategoryId',
          'itemsDeleted',
          'neededItemsAreLoaded',
          'neededItems',
          'neededItemsStores',
          'searchItems',
          'getItemForId',

          'categories',
          'categoriesById',
          'getCategoryForId',
          'categoriesAreLoading',
          'categoriesAreLoaded',
          'categoriesDeleted',

          'meals',
          'mealsAreLoading',
          'mealsAreLoaded',
          'mealsDeleted',
          'getMealForId',

          'stores',
          'storesAreLoading',
          'storesAreLoaded',
          'storesDeleted',
          'getStoreForId'
        ])
      }
    })
  }
}
